import api from '@/api/api.js'
import { DataLoadState } from '@/utils/storeUtils.js'

export const ANIMALS_STORE_NAMESPACE = 'animals'
export const SELECTED_ANIMAL_STORE_NAMESPACE = 'selectedHuntArea'

// Mutation names
const SET_LOAD_STARTED = 'setLoadStarted'
const SET_LOAD_FINISHED_SUCCESSFULLY = 'setLoadFinishedSuccessfully'
const SET_LOAD_FAILED = 'setLoadFailed'
const SET_SELECTED_ANIMAL_ID = 'setSelectedAnimalId'
const UPDATE_DISTRICTS = 'updateDistricts'
const UPDATE_SELECTED_ANIMAL = 'updateSelectedAnimal'

// Getter names
export const IS_LOADING = 'isLoading'
export const GET_LOAD_RESULT = 'getLoadResult'
export const GET_LOAD_ERROR = 'getLoadError'
export const GET_SELECTED_ANIMAL = 'getSelectedAnimal'

// Action names
export const LOAD_ANIMALS = 'loadAnimals'
export const SELECT_ANIMAL_WITH_ID = 'selectAnimalWithId'
export const ADD_ANIMAL = 'addAnimal'
export const SAVE_ANIMAL = 'saveAnimal'
export const DELETE_ANIMAL = 'deleteAnimal'
export const SAVE_ANIMAL_LOCALIZATION = 'saveAnimalLocalization'
export const DELETE_ANIMAL_LOCALIZATION = 'deleteAnimalLocalization'
export const SAVE_ANIMAL_PERIOD = 'saveAnimalPeriod'
export const DELETE_ANIMAL_PERIOD = 'deleteAnimalPeriod'
export const SAVE_ANIMAL_DISTRICT = 'saveAnimalDistrict'
export const UPLOAD_ICON_IMAGE = 'uploadIconImage'

// Users store implementation
export const AnimalsStore = {
  namespaced: true,

  state: () => ({
    // state must be a function to allow reuse of this store
    loadState: new DataLoadState(),
    selectedAnimalId: null
  }),

  mutations: {
    [SET_LOAD_STARTED](state, { loadStartTimestamp }) {
      const dataLoadState = new DataLoadState(loadStartTimestamp)
      state.loadState = dataLoadState.stateForLoadStarted()
    },

    [SET_LOAD_FINISHED_SUCCESSFULLY](state, { loadStartTimestamp, animals }) {
      if (loadStartTimestamp !== state.loadState.id) {
        // Load is stale, ignore
        return
      }

      state.loadState = state.loadState.stateForLoadFinished(animals)
    },

    [SET_LOAD_FAILED](state, { loadStartTimestamp, error }) {
      if (loadStartTimestamp !== state.loadState.id) {
        // Load is stale, ignore
        return
      }

      state.loadState = state.loadState.stateForLoadFailed(error)
    },

    [SET_SELECTED_ANIMAL_ID](state, { animalId }) {
      state.selectedAnimalId = animalId
    },

    [UPDATE_DISTRICTS](state, { animalDistricts }) {
      state.loadState.data.allDistricts = animalDistricts
    },

    [UPDATE_SELECTED_ANIMAL](state, { selectedAnimal }) {
      const animalIndex = state.loadState.data.animals.findIndex(
        animal => animal.animalId === selectedAnimal.animalId
      )
      if (animalIndex >= 0) {
        state.loadState.data.animals[animalIndex] = selectedAnimal

        // Clear and set selectedAnimalId to make sure UI updates
        state.selectedAnimalId = null
        state.selectedAnimalId = selectedAnimal.animalId
      }
    }
  },

  getters: {
    [IS_LOADING]: state => {
      return state.loadState.status.loading
    },

    [GET_LOAD_RESULT]: state => {
      return state.loadState.data
    },

    [GET_LOAD_ERROR]: state => {
      return state.loadState.status.error
    },

    [GET_SELECTED_ANIMAL]: state => {
      if (!state.loadState.data) {
        return null
      }

      if (!state.selectedAnimalId) {
        return null
      }

      return state.loadState.data.animals.find(animal => animal.animalId === state.selectedAnimalId)
    }
  },

  actions: {
    [LOAD_ANIMALS]: async ({ commit }) => {
      // Start load
      const loadStartTimestamp = Date.now()
      commit(SET_LOAD_STARTED, { loadStartTimestamp })

      try {
        const response = await api.get('admin/animals')

        commit(SET_LOAD_FINISHED_SUCCESSFULLY, { loadStartTimestamp, animals: response.data })
      } catch (error) {
        commit(SET_LOAD_FAILED, { loadStartTimestamp, error })
      }
    },

    [SELECT_ANIMAL_WITH_ID]: ({ commit }, { animalId }) => {
      commit(SET_SELECTED_ANIMAL_ID, { animalId })
    },

    [ADD_ANIMAL]: (_, { animal }) => {
      return api.post('admin/animals', animal)
    },

    [SAVE_ANIMAL]: (_, { animal }) => {
      return api.put(`admin/animals/${animal.animalId}`, animal)
    },

    [DELETE_ANIMAL]: (_, { animalId }) => {
      return api.delete(`admin/animals/${animalId}`)
    },

    [SAVE_ANIMAL_LOCALIZATION]: (_, { animalId, animalLocalization }) => {
      return api.put(`admin/animals/${animalId}/localization`, animalLocalization)
    },

    [DELETE_ANIMAL_LOCALIZATION]: (_, { animalId, languageCode }) => {
      return api.delete(`admin/animals/${animalId}/localization/${languageCode}`)
    },

    [SAVE_ANIMAL_PERIOD]: (_, { animalId, animalPeriod }) => {
      return api.put(`admin/animals/${animalId}/period`, animalPeriod)
    },

    [SAVE_ANIMAL_DISTRICT]: async ({ commit }, { animalDistrict }) => {
      if (animalDistrict.id) {
        await api.put(`admin/animals/districts/${animalDistrict.id}`, animalDistrict)
      } else {
        await api.post('admin/animals/districts', animalDistrict)
      }

      const animalsLoadResponse = await api.get('admin/animals')
      const animalDistricts = animalsLoadResponse.data.allDistricts

      commit(UPDATE_DISTRICTS, { animalDistricts })
    },

    [DELETE_ANIMAL_PERIOD]: (_, { animalPeriodId }) => {
      return api.delete(`admin/animals/period/${animalPeriodId}`)
    },

    [UPLOAD_ICON_IMAGE]: async ({ commit, getters }, { dataFile }) => {
      const selectedAnimal = getters[GET_SELECTED_ANIMAL]
      if (!selectedAnimal) {
        return
      }

      const formData = new FormData() // eslint-disable-line
      formData.append('fileContents', dataFile)

      const resourceId = `animal_icon_${selectedAnimal.animalId}`

      await api.post(`admin/resources/${resourceId}/data`, formData)

      const iconURL = `${process.env.VUE_APP_HITUDE_CONNECT_BASE_URL}/resources/${resourceId}/data`

      if (selectedAnimal.iconURL !== iconURL) {
        const selectedAnimalCopy = { ...selectedAnimal }
        selectedAnimalCopy.iconURL = iconURL
        await api.put(`admin/animals/${selectedAnimal.animalId}`, selectedAnimalCopy)

        commit(UPDATE_SELECTED_ANIMAL, { selectedAnimal: selectedAnimalCopy })
      }

      return iconURL
    }
  }
}
